import React from 'react';
import { motion } from 'framer-motion';
import { CurrencyDollarIcon, InformationCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const FeeAnalysisCard = ({ totalFees, feeBreakdown, totalValue, detailed = false }) => {
  const feePercentage = totalValue > 0 ? (totalFees / totalValue) * 100 : 0;
  
  const getFeeLevel = (percentage) => {
    if (percentage <= 0.5) return { level: 'Excellent', color: 'text-green-400', bgColor: 'bg-green-400/10' };
    if (percentage <= 1.0) return { level: 'Good', color: 'text-blue-400', bgColor: 'bg-blue-400/10' };
    if (percentage <= 2.0) return { level: 'Fair', color: 'text-yellow-400', bgColor: 'bg-yellow-400/10' };
    return { level: 'High', color: 'text-red-400', bgColor: 'bg-red-400/10' };
  };

  const feeLevel = getFeeLevel(feePercentage);

  const projectedFeesOverTime = (years) => {
    const annualReturn = 7; // Assume 7% annual return
    let totalFeesOverTime = 0;
    let currentValue = totalValue;
    
    for (let year = 1; year <= years; year++) {
      const yearFee = currentValue * (feePercentage / 100);
      totalFeesOverTime += yearFee;
      currentValue = currentValue * (1 + annualReturn / 100) - yearFee;
    }
    
    return totalFeesOverTime;
  };

  const getFeeInsight = () => {
    if (feePercentage <= 0.5) {
      return "Excellent! Your fees are very low, which means more of your returns stay in your pocket.";
    } else if (feePercentage <= 1.0) {
      return "Good fee structure. You're keeping most of your investment returns.";
    } else if (feePercentage <= 2.0) {
      return "Your fees are reasonable, but there might be opportunities to reduce costs.";
    } else {
      return "High fees drag long-term returns. Switch to lower-cost alternatives.";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gray-800 rounded-lg p-6 ${detailed ? 'col-span-full' : ''}`}
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <CurrencyDollarIcon className="h-5 w-5 text-aurora-blue" />
          Investment Costs
        </h2>
        <div className={`px-3 py-1 rounded-full text-sm font-medium ${feeLevel.bgColor} ${feeLevel.color}`}>
          {feeLevel.level} Fees
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Fee Summary */}
        <div className="space-y-4">
          <div className="bg-gray-700 rounded-lg p-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-white mb-1">
                ${totalFees.toLocaleString()}
              </div>
              <div className="text-gray-400 text-sm">Estimated Annual Fees</div>
              <div className={`text-lg font-semibold ${feeLevel.color} mt-2`}>
                {feePercentage.toFixed(2)}% of portfolio
              </div>
            </div>
          </div>

          <div className="bg-gray-700 rounded-lg p-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-white mb-1">
                ${Math.round(projectedFeesOverTime(10)).toLocaleString()}
              </div>
              <div className="text-gray-400 text-sm">Projected 10-Year Fees</div>
            </div>
          </div>

          <div className="bg-gray-700 rounded-lg p-4">
            <p className="text-white text-sm leading-relaxed">
              {getFeeInsight()}
            </p>
          </div>
        </div>

        {/* Fee Breakdown */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Fee Breakdown</h3>
          
          {feeBreakdown && feeBreakdown.length > 0 ? (
            <div className="space-y-3">
              {feeBreakdown.map((fee, index) => (
                <div key={index} className="bg-gray-700 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-medium text-sm">{fee.symbol}</div>
                      <div className="text-gray-400 text-xs">{fee.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-aurora-blue font-bold">${fee.annual_fee}</div>
                      <div className="text-gray-400 text-xs">{fee.expense_ratio}% ER</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-gray-700 rounded-lg p-4 text-center">
              <InformationCircleIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-400 text-sm">
                No expense ratio data available for your holdings
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Detailed Analysis */}
      {detailed && (
        <div className="mt-8 border-t border-gray-700 pt-6">
          <h3 className="text-lg font-semibold text-white mb-4">Fee Impact Analysis</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-1">
                  ${Math.round(projectedFeesOverTime(5)).toLocaleString()}
                </div>
                <div className="text-gray-400 text-sm">5-Year Fees</div>
              </div>
            </div>
            
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-1">
                  ${Math.round(projectedFeesOverTime(10)).toLocaleString()}
                </div>
                <div className="text-gray-400 text-sm">10-Year Fees</div>
              </div>
            </div>
            
            <div className="bg-gray-700 rounded-lg p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-white mb-1">
                  ${Math.round(projectedFeesOverTime(20)).toLocaleString()}
                </div>
                <div className="text-gray-400 text-sm">20-Year Fees</div>
              </div>
            </div>
          </div>

          {/* Fee Comparison */}
          <div className="bg-gray-700 rounded-lg p-4">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <InformationCircleIcon className="h-4 w-4 text-aurora-blue" />
              Fee Comparison Guide
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-300 mb-2">Low Cost (≤0.5%)</div>
                <div className="text-gray-400">Most index funds and ETFs</div>
              </div>
              <div>
                <div className="text-gray-300 mb-2">Moderate Cost (0.5-1.5%)</div>
                <div className="text-gray-400">Actively managed funds</div>
              </div>
              <div>
                <div className="text-gray-300 mb-2">High Cost (>1.5%)</div>
                <div className="text-gray-400">Specialized or niche funds</div>
              </div>
              <div>
                <div className="text-gray-300 mb-2">Your Portfolio</div>
                <div className={`font-medium ${feeLevel.color}`}>
                  {feePercentage.toFixed(2)}% - {feeLevel.level}
                </div>
              </div>
            </div>
          </div>

          {/* Cost-Saving Tips */}
          {feePercentage > 1.0 && (
            <div className="bg-yellow-400/10 border border-yellow-400/20 rounded-lg p-4 mt-4">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5" />
                <div>
                  <h4 className="text-yellow-400 font-medium mb-2">Cost-Saving Opportunities</h4>
                  <ul className="text-gray-300 text-sm space-y-1">
                    <li>• Switch to lower-cost index funds or ETFs</li>
                    <li>• Review if active management is worth the higher fees</li>
                    <li>• Look for similar funds with lower expense ratios</li>
                    <li>• Even small fee reductions can significantly impact long-term returns</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default FeeAnalysisCard;
